import {
  ArrowDownTrayIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React from "react";

const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith("image/")) {
    return PhotoIcon;
  }
  if (mimeType.startsWith("video/")) {
    return VideoCameraIcon;
  }
  if (mimeType.startsWith("application/pdf")) {
    return DocumentTextIcon;
  }
  return DocumentIcon;
};

interface ChatAttachmentProps {
  fileName: string;
  fileSize: number;
  mimeType: string;
  variant?: "sender" | "receiver" | "compose";
  status?: "pending" | "uploading" | "error" | "uploaded";
  onRemove?: () => void;
  onClick?: () => void;
}

export const ChatAttachment: React.FC<ChatAttachmentProps> = ({
  fileName,
  fileSize,
  mimeType,
  variant = "receiver",
  status = "uploaded",
  onRemove,
  onClick,
}) => {
  const getStatusText = () => {
    if (status === "uploading") return "Uploading...";
    if (status === "error") return "Failed";
    return `${(fileSize / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div
      className={clsx(
        "flex items-center gap-2 rounded-lg pl-2 pr-3 py-2",
        onClick && "cursor-pointer hover:opacity-80",
        variant === "sender" && "bg-limestone-800",
        variant === "receiver" && "bg-bg-pressed",
        variant === "compose" && "bg-bg-pressed",
      )}
      onClick={onClick}
    >
      <div
        className={clsx(
          "flex items-center justify-center w-8 h-8 rounded-md shrink-0",
          variant === "sender" ? "bg-teal-600" : "bg-teal-700",
        )}
      >
        {React.createElement(getFileIcon(mimeType), {
          className: "h-4 w-4 text-white",
        })}
      </div>
      <div className="flex flex-col min-w-0">
        <span
          className={clsx(
            "text-sm font-medium truncate max-w-[180px] ph-no-capture",
            variant === "sender" ? "text-white" : "text-text-primary",
          )}
        >
          {fileName}
        </span>
        <span
          className={clsx(
            "text-xs",
            variant === "sender" ? "text-limestone-300" : "text-text-tertiary",
            status === "error" && "text-red-500",
          )}
        >
          {getStatusText()}
        </span>
      </div>
      {onRemove ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-2 p-1 hover:bg-text-tertiary rounded-full transition-colors"
          aria-label="Remove attachment"
        >
          <XMarkIcon className="h-4 w-4 text-text-tertiary" />
        </button>
      ) : (
        <ArrowDownTrayIcon
          className={clsx(
            "h-4 w-4 shrink-0 ml-auto",
            variant === "sender" ? "text-limestone-300" : "text-limestone-600",
          )}
        />
      )}
    </div>
  );
};
